import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from "chart.js";
import defaultLineChartConfigs from "examples/Charts/LineCharts/DefaultLineChart/configs";
import typography from "assets/theme/base/typography";
import colors, { SBI_DARK, SBI_LIGHT } from "assets/theme/base/colors";
import { useMaterialUIController } from "context";
import { FRESENIUS } from "contextBranding";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Generate month labels for the last 12 months
const generateMonthLabels = () => {
  const labels = [];
  const today = new Date();
  for (let i = 11; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const monthName = date.toLocaleDateString("en-US", { month: "short" });
    labels.push(monthName);
  }
  return labels;
};

// Generate data with gradual improvements over time
const generateTimelineData = () => {
  const labels = generateMonthLabels();

  // Current values (from the image)
  const currentValues = {
    noDelirium: 60,
    noNausea: 50,
    noHighPain: 100,
    noHighDiscomfort: 90,
    quattro: 70,
  };

  // Starting values (12 months ago) - lower than current, showing improvement
  const startingValues = {
    noDelirium: 45,
    noNausea: 25,
    noHighPain: 95,
    noHighDiscomfort: 75,
    quattro: 50,
  };

  // Generate data points with gradual improvement
  const generateDataPoints = (startValue, endValue) => {
    const points = [];
    for (let i = 0; i < 12; i++) {
      // Linear interpolation with slight randomness for realism
      const progress = i / 11;
      const randomVariation = (Math.random() - 0.5) * 2; // ±1% variation
      const value =
        startValue + (endValue - startValue) * progress + randomVariation;
      // Clamp values between 0 and 100
      points.push(Math.max(0, Math.min(100, Math.round(value * 10) / 10)));
    }
    return points;
  };

  return {
    labels,
    datasets: [
      {
        label: "No Delirium / PND",
        data: generateDataPoints(
          startingValues.noDelirium,
          currentValues.noDelirium
        ),
        // Using a custom color that will be mapped
        customColor: "#B0B0B0", // Light grey
      },
      {
        label: "No Nausea / Vomiting",
        data: generateDataPoints(
          startingValues.noNausea,
          currentValues.noNausea
        ),
        customColor: "#4ECDC4", // Teal/light blue
      },
      {
        label: "No High Pain",
        data: generateDataPoints(
          startingValues.noHighPain,
          currentValues.noHighPain
        ),
        customColor: "#424242", // Dark grey
      },
      {
        label: "No High Discomfort",
        data: generateDataPoints(
          startingValues.noHighDiscomfort,
          currentValues.noHighDiscomfort
        ),
        customColor: "#212121", // Darker grey/black
      },
      {
        label: "QUATTRO",
        data: generateDataPoints(startingValues.quattro, currentValues.quattro),
        customColor: "#FF9800", // Orange
      },
    ],
  };
};

function getDatasetColor(customColor, isFreseniusBranding) {
  if (isFreseniusBranding && customColor === "#4ECDC4") return FRESENIUS.blueLight;
  return customColor;
}

function QuattroMetricsTimeline({ isFreseniusBranding = false }) {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const isLight = controller?.sbiDashboardTheme === "light";
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const chartData = useMemo(() => generateTimelineData(), []);
  const sb = colors.backgroundSbi;
  const themeColors = isLight ? SBI_LIGHT : SBI_DARK;
  const freseniusTheme = isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark;
  const cardBg = isFreseniusBranding
    ? (isLight ? freseniusTheme.bgHeader : `${FRESENIUS.blueDark}ee`)
    : (isLight ? themeColors.bgHeader : `${sb.default}99`);
  const cardBorder = isFreseniusBranding
    ? (isLight ? FRESENIUS.blue : freseniusTheme.border)
    : (isLight ? themeColors.border : `${sb.default}80`);

  // Prepare datasets with custom colors using template's structure
  const chartDatasets = useMemo(() => {
    return chartData.datasets.map((dataset) => {
      const color = getDatasetColor(dataset.customColor, isFreseniusBranding);
      return {
        label: dataset.label,
        data: dataset.data,
        tension: 0,
        pointRadius: 0,
        borderWidth: 3,
        backgroundColor: "transparent",
        fill: true,
        pointBackgroundColor: color,
        borderColor: color,
        maxBarThickness: 6,
        customColor: color,
      };
    });
  }, [chartData, isFreseniusBranding]);

  // Use template's configs but customize to show legend
  const { data, options: baseOptions } = useMemo(
    () => defaultLineChartConfigs(chartData.labels, chartDatasets),
    [chartData.labels, chartDatasets]
  );

  // Extend options - use default Chart.js legend
  const chartOptions = useMemo(
    () => ({
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: {
          display: false,
          // position: "top", // Options: "top", "bottom", "left", "right", "chartArea"
          // align: "center", // Options: "start", "center", "end"
          // labels: {
          //   usePointStyle: true, // Use point style instead of box
          //   padding: 15,
          //   font: {
          //     size: 11,
          //     family: typography.fontFamily,
          //   },
          //   color: "#67748e",
          //   boxWidth: 12,
          //   boxHeight: 12,
          // },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(
                1
              )}%`;
            },
          },
        },
      },
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          beginAtZero: true,
          max: 110, // Show padding above 100% for better visualization
          afterBuildTicks: function (axis) {
            // Manually set ticks to ensure 100% is always included
            axis.ticks = [0, 20, 40, 60, 80, 100].map((value) => ({ value }));
          },
          ticks: {
            ...baseOptions.scales.y.ticks,
            callback: function (value) {
              // Show labels up to and including 100%
              if (value > 100) return "";
              return value + "%";
            },
          },
        },
      },
    }),
    [baseOptions]
  );

  return (
    <Card
      sx={(theme) => ({
        height: "100%",
        borderRadius: theme.borders.borderRadius.lg,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        backdropFilter: "blur(12px)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
      })}
    >
      <MDBox
        p={3}
        display="flex"
        flexDirection="column"
        height="100%"
        justifyContent="space-between"
      >
        <MDBox>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <MDTypography
              variant="h5"
              fontWeight="bold"
              color={isFreseniusBranding ? "white" : "dark"}
            >
              QUATTRO METRICS
            </MDTypography>
            <Icon
              sx={{
                fontSize: "1.25rem",
                color: "text.secondary",
                cursor: "pointer",
              }}
            >
              info
            </Icon>
          </MDBox>

          <MDBox height="300px" mt={0}>
            <Line data={data} options={chartOptions} />
          </MDBox>

          {/* Improvement/Decline Summary */}
          <MDBox mt={1} mb={2}>
            <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
              {chartDatasets.map((dataset, index) => {
                const firstValue = dataset.data[0];
                const lastValue = dataset.data[dataset.data.length - 1];
                const change = lastValue - firstValue;
                const changePercent = ((change / firstValue) * 100).toFixed(1);
                const isImprovement = change >= 0;
                const isHovered = hoveredIndex === index;

                // Calculate approximate widths for smooth animation
                // Base width: dot (12px) + margin (6px) + icon (~16px) + gap (4px) + percentage text (~50px) + padding (20px) ≈ 108px
                // Expanded width: base + label text (varies, max ~200px) + margin (6px) ≈ 314px
                const baseWidth = 108; // Compact size (dot + icon + percentage)
                const expandedWidth = 230; // Expanded size (includes label text)

                return (
                  <MDBox
                    key={index}
                    display="inline-flex"
                    alignItems="center"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    sx={(theme) => ({
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: isImprovement
                        ? (isFreseniusBranding
                          ? `${FRESENIUS.blueLight}20`
                          : `${theme.palette.statusSbi.success}20`)
                        : `${theme.palette.statusSbi.error}20`,
                      border: `1px solid ${
                        isImprovement
                          ? (isFreseniusBranding
                            ? FRESENIUS.blueLight
                            : theme.palette.statusSbi.success)
                          : theme.palette.statusSbi.error
                      }`,
                      cursor: "pointer",
                      width: isHovered
                        ? `${expandedWidth}px`
                        : `${baseWidth}px`,
                      minWidth: isHovered
                        ? `${expandedWidth}px`
                        : `${baseWidth}px`,
                      maxWidth: isHovered
                        ? `${expandedWidth}px`
                        : `${baseWidth}px`,
                      transition:
                        "width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      },
                    })}
                  >
                    <MDBox
                      sx={{
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: dataset.borderColor,
                        border: `2px solid ${dataset.borderColor}`,
                        mr: 0.75,
                      }}
                    />
                    {/* Animated label text - positioned between dot and percentage */}
                    <MDBox
                      sx={{
                        width: isHovered ? "auto" : "0",
                        minWidth: isHovered ? "auto" : "0",
                        maxWidth: isHovered ? "300px" : "0",
                        opacity: isHovered ? 1 : 0,
                        overflow: "hidden",
                        transition:
                          "width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease, opacity 0.3s ease, margin 0.3s ease",
                        whiteSpace: "nowrap",
                        mr: isHovered ? 0.75 : 0,
                        flexShrink: 0,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                      >
                        {dataset.label}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ flexShrink: 0 }}
                    >
                      <Icon
                        sx={(theme) => ({
                          fontSize: "0.875rem",
                          color: isImprovement
                            ? (isFreseniusBranding
                              ? FRESENIUS.blueLight
                              : theme.palette.statusSbi.success)
                            : theme.palette.statusSbi.error,
                        })}
                      >
                        {isImprovement ? "trending_up" : "trending_down"}
                      </Icon>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={(theme) => ({
                          color: isImprovement
                            ? (isFreseniusBranding
                              ? FRESENIUS.blueLight
                              : theme.palette.statusSbi.success)
                            : theme.palette.statusSbi.error,
                        })}
                      >
                        {isImprovement ? "+" : ""}
                        {changePercent}%
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Dashboard Button */}
        <MDBox>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => navigate("/sbidark")}
            startIcon={<Icon>dashboard</Icon>}
            fullWidth
            sx={(theme) => ({
              background: isFreseniusBranding
                ? `linear-gradient(135deg, ${FRESENIUS.blue} 0%, ${FRESENIUS.blueLight} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primarySbi.main} 0%, ${theme.palette.primarySbi.focus} 100%)`,
              "&:hover": {
                background: isFreseniusBranding
                  ? `linear-gradient(135deg, ${FRESENIUS.blueLight} 0%, ${FRESENIUS.blue} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.primarySbi.focus} 0%, ${theme.palette.primarySbi.main} 100%)`,
              },
            })}
          >
            Go to Dashboard
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default QuattroMetricsTimeline;
